import { Button, Card, Heading, Text, Tooltip } from '@chakra-ui/react'
import type React from 'react'
import type { PropsWithChildren, ReactNode } from 'react'

type DangerCardProps = {
  /**
   * Card title ('Danger Zone' by default)
   */
  title?: string
  /**
   * Description text inside the danger zone.
   */
  description?: ReactNode
  /**
   * Description shown if disabled state is active and user hovers over the element.
   */
  tooltipDescription?: string
  /**
   * Function that is executed upon clicking the button inside the dangerzone.
   */
  onAction: () => void
  /**
   * Text inside the button of the danger zone.
   */
  dangerousActionText: string
  /**
   * Disables the whole danger zone.
   */
  isDisabled?: boolean
} & React.ComponentProps<typeof Card.Root>

export function DangerCard({
  title,
  description,
  tooltipDescription,
  children,
  dangerousActionText,
  onAction,
  isDisabled,
  ...cardProps
}: PropsWithChildren<DangerCardProps>) {
  return (
    <>
      <Card.Root
        width="100%"
        variant="outline"
        borderColor="border.border-error-strong"
        bg="background.bg-base"
        {...cardProps}
      >
        <Card.Header mb={0} pb={0}>
          <Heading as="h2" color="text.text-error-strong" m={0}>
            {title || 'Danger Zone'}
          </Heading>

          {description && (
            <Text m={0} mt={2}>
              {description}
            </Text>
          )}
        </Card.Header>

        <Card.Body>
          {children}
          <Tooltip.Root disabled={!isDisabled}>
            <Tooltip.Trigger asChild>
              <Button
                variant="solid"
                colorPalette="red"
                disabled={isDisabled}
                onClick={() => onAction()}
                data-testid="dangerous-action-open"
              >
                {dangerousActionText}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>{tooltipDescription}</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        </Card.Body>
      </Card.Root>
    </>
  )
}
